import React from "react";
import PropTypes from "prop-types";

import { Divider, Grid, MenuItem, Select, TextField } from "@mui/material";

import MDTypography from "components/MDTypography";

/**
 * The page-level fields of a website page — everything except its content
 * blocks, which the SectionEditor handles.
 */
const PageFormFields = ({ values, errors, onChange, isUpdate }) => {
  const text = (name, label, options = {}) => (
    <TextField
      fullWidth
      label={label}
      value={values[name] ?? ""}
      onChange={(event) => onChange(name, event.target.value)}
      error={Boolean(errors[name])}
      helperText={errors[name] || options.helperText || ""}
      multiline={Boolean(options.rows)}
      rows={options.rows}
      placeholder={options.placeholder}
      required={options.required}
    />
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        {text("name", "Page name", {
          required: true,
          helperText: "Shown in this panel, e.g. CSR / Sustainability",
        })}
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("slug", "URL key", {
          placeholder: "csr",
          helperText: isUpdate
            ? "The key the website fetches this page by — changing it breaks that link until the website is updated"
            : "Left blank, it is generated from the page name",
        })}
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("page_route", "Website route", {
          placeholder: "/csr",
          helperText: "For reference in this panel",
        })}
      </Grid>

      <Grid item xs={12} sm={6}>
        <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
          Status
        </MDTypography>
        <Select
          fullWidth
          value={values.status || "active"}
          onChange={(event) => onChange("status", event.target.value)}
          sx={{ height: "45px" }}
        >
          <MenuItem value="active">Active — the website uses this content</MenuItem>
          <MenuItem value="inactive">Inactive — hidden</MenuItem>
        </Select>
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("title", "Page heading")}
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("subtitle", "Page sub-heading", { rows: 2 })}
      </Grid>

      <Grid item xs={12}>
        <Divider />
        <MDTypography variant="h6">Search engine details</MDTypography>
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("seo_title", "Meta title")}
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("seo_keywords", "Meta keywords")}
      </Grid>

      <Grid item xs={12}>
        {text("seo_description", "Meta description", { rows: 2 })}
      </Grid>
    </Grid>
  );
};

PageFormFields.defaultProps = {
  errors: {},
  isUpdate: false,
};

PageFormFields.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  isUpdate: PropTypes.bool,
};

export default PageFormFields;
